import {http} from '../http'
import * as corePersons from '../../coreApi/persons'
import {APIResponse} from '../types'
import {handleError} from '../handleError'
import {RawPersonName} from '../../coreApi/persons/getNames'

// Max person IDs per GetNames request. GetNames isn't paginated; this bounds the
// URL length (one `id=` query param per ID), not a page count. Mirrors the
// batching in api/staff/fetchHiredStaff.
const NAMES_BATCH_SIZE = 100

interface Params {
  clientID: number
  ids: number[]
}

// Resolved names as a personId → display-name map, ready for the UI/CSV to look
// up. Names are a display enhancement, so callers treat a failure as
// non-fatal (fall back to "Person <id>").
type GetNamesReturn = APIResponse<Map<number, string>>

const ERROR_PATH = 'api.persons.getNames()'
const GENERIC_ERROR =
  'Something went wrong while loading names. Please try again.'

// Build the display name as `First "Preferred" Last` when a preferred name is
// present, else `First Last` (mirrors api/staff/fetchHiredStaff's
// toHiredStaffMember). Blank pieces are dropped so we never emit stray spaces.
const toDisplayName = (person: RawPersonName): string => {
  const first = person.First ?? ''
  const last = person.Last ?? ''
  const preferred = person.Preferred?.trim() || null
  const pieces = preferred ? [first, `"${preferred}"`, last] : [first, last]
  return pieces.filter(Boolean).join(' ')
}

/**
 * Resolve display names for a list of person IDs via the persons GetNames batch
 * endpoint (client-scoped, no per-person IDOR check — the same call the staff
 * roster uses). The distinct IDs are de-duplicated and chunked at
 * NAMES_BATCH_SIZE so an unbounded id list can't blow the URL length; each
 * chunk fires in parallel and the results merge into a single name map.
 */
export const getNames = async ({
  clientID,
  ids,
}: Params): Promise<GetNamesReturn> => {
  try {
    const distinctIds = Array.from(new Set(ids))
    if (distinctIds.length === 0) {
      return {result: 'success', data: new Map()}
    }

    const batches: number[][] = []
    for (let i = 0; i < distinctIds.length; i += NAMES_BATCH_SIZE) {
      batches.push(distinctIds.slice(i, i + NAMES_BATCH_SIZE))
    }

    const responses = await Promise.all(
      batches.map((batch) =>
        corePersons.getNames(http, {ClientID: clientID, IDs: batch}),
      ),
    )

    const nameMap = new Map<number, string>()
    responses.forEach((response) => {
      response.data.forEach((person) => {
        nameMap.set(person.PersonID, toDisplayName(person))
      })
    })

    return {result: 'success', data: nameMap}
  } catch (e) {
    return handleError(ERROR_PATH, GENERIC_ERROR, e)
  }
}
